import { ArrowDropDown } from "@mui/icons-material";
import { Button, Menu, MenuItem, Skeleton, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import {
  useFeatureFlagPayload,
  useFeatureFlagVariantKey,
} from "posthog-js/react";
import { useEffect, useMemo, useState } from "react";
import { useStore } from "../../stores";
import type { AgentAssistant } from "../../stores/agent";
import {
  AssistantAccessConfig,
  parseAssistantAccessConfig,
} from "src/utils/assistantAccess";

interface AssistantSelectorProps {
  onAssistantChange?: (assistantId: string | null) => void;
}

const ASSISTANT_ACCESS_FLAG = "enabled-assistant-ids";

const getEnvironmentAssistantAccessConfig = (): AssistantAccessConfig => {
  const defaultAssistantId = window._env_.ASSISTANT_ID || null;
  return {
    assistantIds: defaultAssistantId ? [defaultAssistantId] : null,
    defaultAssistantId,
  };
};

export const AssistantSelector = observer(function AssistantSelector({
  onAssistantChange,
}: AssistantSelectorProps) {
  const { agentStore } = useStore();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const assistantAccessPayload = useFeatureFlagPayload(ASSISTANT_ACCESS_FLAG);
  const assistantAccessVariant = useFeatureFlagVariantKey(
    ASSISTANT_ACCESS_FLAG
  );
  const assistantAccessConfig = useMemo(
    (): AssistantAccessConfig | null => {
      if (assistantAccessVariant === undefined) {
        return null;
      }

      if (assistantAccessVariant === false) {
        return getEnvironmentAssistantAccessConfig();
      }

      const parsedConfig = parseAssistantAccessConfig(assistantAccessPayload);
      if (parsedConfig) {
        return parsedConfig;
      }

      return getEnvironmentAssistantAccessConfig();
    },
    [assistantAccessPayload, assistantAccessVariant]
  );
  const assistantIdsDependency =
    assistantAccessConfig?.assistantIds?.join("\u0000") ??
    (assistantAccessConfig?.assistantIds === null ? "all" : "unset");

  useEffect(() => {
    // Initialize assistant selection and fetch available assistants
    agentStore.initializeAssistantSelection();
    if (!assistantAccessConfig) {
      return;
    }

    agentStore.getAvailableAssistants(assistantAccessConfig);
  }, [
    agentStore,
    assistantAccessConfig?.defaultAssistantId,
    assistantIdsDependency,
  ]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!agentStore.assistantsLoading) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAssistantSelect = (assistantId: string) => {
    agentStore.setSelectedAssistantId(assistantId);
    onAssistantChange?.(assistantId);
    handleClose();
  };

  const getDisplayName = (assistant: AgentAssistant): string => {
    return assistant.name;
  };

  const selectedAssistant = agentStore.selectedAssistant;
  const selectedDisplayName = selectedAssistant
    ? getDisplayName(selectedAssistant)
    : "Select Assistant";

  if (
    (agentStore.assistantsLoading || !assistantAccessConfig) &&
    agentStore.availableAssistants.length === 0
  ) {
    return (
      <div className="min-w-[120px]">
        <Skeleton height={32} width="100%" />
      </div>
    );
  }

  return (
    <>
      <Button
        variant="text"
        size="small"
        onClick={handleClick}
        endIcon={<ArrowDropDown />}
        disabled={agentStore.assistantsLoading}
        sx={{
          minWidth: "auto",
          fontSize: "0.75rem",
          padding: "4px 8px",
          borderColor: "rgba(0, 0, 0, 0.23)",
          color: "rgba(0, 0, 0, 0.6)",
          "&:hover": {
            borderColor: "rgba(0, 0, 0, 0.23)",
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          },
          "&:disabled": {
            color: "rgba(0, 0, 0, 0.38)",
          },
        }}
      >
        <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
          {selectedDisplayName}
        </Typography>
      </Button>

      <Menu
        id="assistant-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "assistant-button",
        }}
        disablePortal
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        {agentStore.availableAssistants.length === 0 ? (
          <MenuItem disabled>
            <Typography color="text.secondary">
              No assistants available
            </Typography>
          </MenuItem>
        ) : (
          agentStore.availableAssistants.map((assistant) => (
            <MenuItem
              key={assistant.assistant_id}
              onClick={() => handleAssistantSelect(assistant.assistant_id)}
              selected={
                assistant.assistant_id === agentStore.selectedAssistantId
              }
            >
              <div className="flex flex-col">
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {getDisplayName(assistant)}
                </Typography>
                {assistant.description && (
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    {assistant.description}
                  </Typography>
                )}
              </div>
            </MenuItem>
          ))
        )}
      </Menu>
    </>
  );
});
